import type { ReactNode } from "react";

import { Button } from "../f7";
/**
 * The design's chart primitives: a distribution bar row and the headline tile.
 *
 * Neither product serves a history route, so there is no sparkline: a line
 * through one day's point would be decoration pretending to be a trend.
 */

export interface BarRow {
  label: string;
  value: number;
  note?: string;
  tone?: "brand" | "warn" | "bad" | "mute";
}

/** Horizontal distribution bars — DOM rather than SVG, so text stays selectable. */
export function Bars({ rows, max }: { rows: BarRow[]; max?: number }) {
  const ceiling = max ?? Math.max(...rows.map((r) => r.value), 1);
  const fill = {
    brand: "bg-brand-500",
    warn: "bg-amber-500",
    bad: "bg-red-500",
    mute: "bg-slate-400 dark:bg-slate-500",
  };
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs/5 text-slate-950 dark:text-white">{r.label}</span>
            <span className="font-mono text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
              {r.value.toLocaleString()}
              {r.note ? ` · ${r.note}` : ""}
            </span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-950/5 dark:bg-white/10">
            <div
              className={`h-full rounded-full ${fill[r.tone ?? "brand"]}`}
              style={{ width: `${Math.round((r.value / ceiling) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** The design's headline tile: label, figure, what the figure covers, window, note. */
export function MetricTile({
  label,
  value,
  note,
  detail,
  from,
  to,
  aside,
  onRetry,
}: {
  label: string;
  value: string;
  note: string;
  /** What the figure covers — said in the place a design draws a trend. */
  detail: string;
  from: string;
  to: string;
  /** A badge or tag on the label row. */
  aside?: ReactNode;
  /** Re-read this tile after a lookup failure. */
  onRetry?: () => void;
}) {
  return (
    <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10">
      <p className="flex items-center gap-1.5 text-xs/5 text-slate-500 dark:text-slate-400">
        {label}
        {aside}
      </p>
      <p className="mt-1 text-2xl/8 font-semibold tabular-nums text-slate-950 dark:text-white">
        {value}
      </p>
      <p className="mt-2 flex h-6 items-center text-[10px]/4 text-slate-400 dark:text-slate-500">
        {detail}
      </p>
      <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-slate-950/5 pt-1 font-mono text-[10px] tabular-nums text-slate-400 dark:border-white/10 dark:text-slate-500">
        <span>{from}</span>
        <span>{to}</span>
      </div>
      <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">{note}</p>
      {onRetry && (
        <Button outline onClick={onRetry} className="mt-2">
          Try again
        </Button>
      )}
    </div>
  );
}
